import React, { ReactNode, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useStatusEditProjects } from '../../../hooks/useStatusEditProjects';
import { setterGantItems } from '../../../store/slices/gantItemsSlice';
import { setterGant } from '../../../store/slices/gantSlice';
import { ItemType, MappingItemIdIndexType } from '../../../types';
import { DragStrategy } from '../drag/dragStrategy';
import { useAppendChildItem } from '../hook/useAppendChildItem';
import { useProjectsTreeDrag } from '../hook/useProjectsTreeDrag';
import { useProjectsTreePopover } from '../hook/useProjectsTreePopover';
import { useRowsMap } from '../hook/useRowsMap';
import { ProjectsTreeContext } from '../projectsTreeContext';
import { ProjectTreeRecordType } from '../projectsTreeTypes';
import { ProjectsTreePopover } from './projectsTreePopover';

export interface ProjectItemsTreeProps {
    data: ItemType[];
    mappingItemIdIndex: MappingItemIdIndexType;
    renderProject: (project: ProjectTreeRecordType) => React.ReactNode;
    renderProjectChildren: (tree: ProjectTreeRecordType[], project: ProjectTreeRecordType) => React.ReactNode;
}

export const ProjectsTreeLayout: React.FC<ProjectItemsTreeProps> = ({
    data,
    mappingItemIdIndex,
    renderProject,
    renderProjectChildren,
}) => {
    const dispatch = useDispatch();

    const { rowsMap, registerCell, unregisterCell } = useRowsMap();
    const { popoverRef, onMouseLeave, onMouseEnter } = useProjectsTreePopover(rowsMap);
    const editProjectStatuses = useStatusEditProjects();

    const cellDomHandlers = useProjectsTreeDrag(rowsMap, popoverRef, data, mappingItemIdIndex);
    useAppendChildItem(data, mappingItemIdIndex);

    return useMemo(() => {
        const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
            dispatch(
                setterGantItems({
                    isItemEdit: true,
                    currentGantItem: data[mappingItemIdIndex[DragStrategy.getItemId(e)]],
                }),
            );
        };

        const getTree = (parentId: string | null, level: number): any[] => {
            return data
                .filter((item) => item.parentId === parentId && !item.hideForCollapse)
                .map((item) => {
                    const collectChildren = (parentId: string, level: number): any[] => {
                        return data
                            .filter((child) => child.parentId === parentId && !child.hideForCollapse)
                            .flatMap((child) => [
                                {
                                    key: child.id,
                                    numberWBS: child.numberWBS ? `${child.numberWBS}.` : null,
                                    level,
                                    children: [],
                                    item: child,
                                },
                                ...collectChildren(child.id, level + 1),
                            ]);
                    };

                    return {
                        key: item.id,
                        numberWBS: item.numberWBS ? `${item.numberWBS}.` : null,
                        level,
                        children: collectChildren(item.id, level + 1),
                        item,
                    };
                });
        };

        const tree = getTree(null, 0);

        const children: ReactNode[] = [];

        const handleShowListTask = (itemId: string) => () => {
            const item = data[mappingItemIdIndex[itemId]];
            if (item) {
                dispatch(
                    setterGant({
                        scheduleItemForListTaskShow: item,
                        dayForListTaskShow: item.children?.length ? [item.startDate, item.endDate] : [],
                    }),
                );
            }
        };

        for (const project of tree) {
            children.push(<React.Fragment key={`project-${project.item.id}`}>{renderProject(project)}</React.Fragment>);
            children.push(
                <div
                    key={`project-children-${project.item.id}`}
                    style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                >
                    {renderProjectChildren(project.children, project)}
                </div>,
            );
        }

        return (
            <ProjectsTreeContext.Provider
                value={{
                    editProjectStatuses,
                    registerCell,
                    unregisterCell,
                    sharedCellProps: {
                        onMouseEnter,
                        onMouseLeave,
                        onClick,
                        ...cellDomHandlers,
                    },
                }}
            >
                <div style={{ width: '100%', paddingTop: 1 }}>{children}</div>
                <ProjectsTreePopover ref={popoverRef} handleShowListTask={handleShowListTask} />
            </ProjectsTreeContext.Provider>
        );
    }, [data]);
};
